import { OmniaEvent } from "../../api/types"
import { OmniaObjectType } from "../../generated/enums"
import { labels } from "../../generated/labels"
import { Layer } from "../../map/layer"
import { isArray, isObject } from "../../utils/utils"
import { EventInfoService } from "./eventInfoService"
import { OmniaObjectType as ObjectType } from "../../generated/enums"

export interface EventText {
    text: string
    cssClass?: string
}

type EventFormatter = (event: OmniaEvent) => EventText

export function getEventTextProvider(eventInfo: EventInfoService) {

    return getProvider(eventInfo, {
        c: "New",
        u: "Updated",
        d: "Deleted",
        export: "Exported",
        import: "Imported",
        calc: "Calculated"
    })

}

export function getActivityTextProvider(eventInfo: EventInfoService) {

    return getProvider(eventInfo, {
        c: "Creating",
        u: "Updating",
        export: "Exporting",
        import: "Importing",
        calc: "Calculating"
    })

}

function getProvider(eventInfo: EventInfoService, standardActions: { [key: string]: string }) {


    const objectNameProviders: { [key: number]: (event: OmniaEvent) => string } = {

        [ObjectType.Field]: getFieldName,
        [ObjectType.Cropping]: getCroppingName,

        [ObjectType.SeedStock]: getStockName,
        [ObjectType.FertiliserStock]: getStockName,
        [ObjectType.ManureStock]: getStockName,

        [ObjectType.SeedPlanMap]: getSeedRatePlanName,

        // TODO
        // [ObjectType.SoilAnalysis]: getSoilAnalysisName,
        // [ObjectType.PcnAnalysis]: getPcnAnalysisName,
        // [ObjectType.HsAnlaysis]: getHsaName,
        // [ObjectType.NitrogenAnalysis]: getNAnalysisName,

    }

    const objectTypeLabels: { [key: number]: string } = {

        [OmniaObjectType.PcnAnalysis]: "PCN analysis",

    }

    const formatters: EventFormatter[] = [

        formatLayerEvent,
        formatBasicEditEvent,
        formatGenericEvent

    ]

    return (event: OmniaEvent): EventText => {

        if (!event) return undefined

        for (const f of formatters) {
            const r = f(event)
            if (r) return r
        }

    }



    function formatLayerEvent(e: OmniaEvent) {

        if (e.ObjectType != ObjectType.Layer) return undefined

        const action = standardActions[e.Action || ""]
        if (!action) return undefined

        let layer = isObject(e.Data) ? e.Data : {}
        let layers = isArray(layer.Layers) ? layer.Layers as {}[] : undefined

        if (layers?.length > 1) {
            return { text: `${action} ${layers.length} layers` }
        }

        if (isObject(layer.Layer)) {
            layer = layer.Layer
        }
        else if (layers) {
            layer = layers[0]
        }

        if (!layer) {
            return undefined
        }

        const field = eventInfo.getFieldDescription(layer.FieldId || 0)
        const type = labels?.LayerTypeId(layer.TypeId || 0) as string
        const name = layer.Name

        const text = [
            action,
            type ? " '" + type + "'" : "",
            " layer",
            name ? ` '${name}'` : "",
            field ? ` on field ${field}` : ""
        ].join("")

        return { text }

    }

    function formatBasicEditEvent(e: OmniaEvent) {

        if (!e.ObjectType) return undefined

        const action = standardActions[e.Action || ""]
        if (!action) return undefined

        let type: string = objectTypeLabels[e.ObjectType]

        if (!type) {
            type = labels.OmniaObjectType(e.ObjectType)
            if (!type) return undefined
            type = type.toLocaleLowerCase()
        }

        const nameProvider = objectNameProviders[e.ObjectType]
        const name = nameProvider ? nameProvider(e) : e.Data?.Name

        let text = `${action} ${type}`

        text = name ? `${text}: ${name}` : text

        return {
            text
        }
    }

    function formatGenericEvent(e: OmniaEvent) {

        const source = labels.OmniaModuleId(e.Source)

        const text = source
            ? `Change from the ${source} module`
            : `Unspecified change`

        return { text, cssClass: 'text-muted' }

    }

    function getCroppingName(e: OmniaEvent): string {

        if (isObject(e.Data)) {

            const name = e.Data?.Field.Name
            const year = e.Data.YearId

            if (name && year) {

                const area = e.Data?.Area
                const crop = e.Data?.Crop?.Name

                if (crop && area) {
                    return `${name} (${area} ha) - ${year} - ${crop}`
                }

                if (area) {
                    return `${name} (${area} ha) - ${year}`
                }

                `${name} - ${year}`

            }

        }

    }

    function getFieldName(e: OmniaEvent): string {

        if (isObject(e.Data) && e.Data["Name"]) {
            const { Name: name, Area: area } = e.Data
            return area ? `${name} (${area} ha)` : name
        }

    }

    function getStockName(e: OmniaEvent): string {
        if (isObject(e.Data)) {
            return e.Data?.Name
        }
    }

    function getSeedRatePlanName(e: OmniaEvent): string {

        if (isObject(e.Data) && e.Data.Name) {

            const croppingId = e.Data["CroppingId"]
            if (croppingId) {
                const d = eventInfo.getCroppingDescription(croppingId)
                return `${d} in plan ${e.Data.Name}`
            }

            return e.Data.Name
        }

    }

}
